import { Component, DestroyRef, OnInit, computed, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { KENDO_BUTTON } from "@progress/kendo-angular-buttons";
import { KENDO_COMBOBOX } from "@progress/kendo-angular-dropdowns";
import { KENDO_TEXTBOX } from "@progress/kendo-angular-inputs";
import { plusIcon, trashIcon } from "@progress/kendo-svg-icons";
import { EditOption } from "../../../../models/edit-option.model";
import { CrewArray, crewRow } from "./crew-members-group";
import { FlightEditLookups } from "../../../form/flight-edit-lookups";
import { ChangedFieldDirective } from "../../../changed-field.directive";

/** Any number of crew members, each picked among the organization's or typed, and each with a role. */
@Component({
    selector: "app-crew-members-group",
    imports: [ChangedFieldDirective, ReactiveFormsModule, TranslatePipe, KENDO_BUTTON, KENDO_COMBOBOX, KENDO_TEXTBOX],
    templateUrl: "./crew-members-group.component.html",
    styleUrl: "../../../edit-fields.scss",
})
export class CrewMembersGroupComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    readonly rows = input.required<CrewArray>();
    readonly lookups = input.required<FlightEditLookups>();
    /** The organization's crew members, and any one already on the flight that is no longer active. */
    readonly members = input.required<EditOption[]>();

    readonly icons = { add: plusIcon, remove: trashIcon };

    /** The crew member each row has picked, by row; null for one typed by hand or not chosen yet. */
    private readonly chosen = signal<(number | null)[]>([]);

    /** What each row offers: the members no other row has picked, and its own. A member leaves the list once picked. */
    readonly options = computed(() => {
        const chosen = this.chosen();
        return chosen.map((own) => this.members().filter((member) => member.id === own || !chosen.includes(member.id)));
    });

    ngOnInit(): void {
        const update = () => this.chosen.set(this.rows().controls.map((row) => row.controls.crewId.value));
        update();
        // Picking, clearing, adding and removing a row all change the value of the array.
        this.rows().valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(update);
    }

    add(): void {
        this.rows().push(crewRow(null, this.lookups(), this.destroyRef));
    }

    remove(index: number): void {
        this.rows().removeAt(index);
    }
}
